import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { AnalyticsChart } from "@/components/AnalyticsChart"
import { CustomersPerCouncilChart } from "@/components/CustomersPerCouncilChart"
import { Download, FileSpreadsheet, FileText, Image } from "lucide-react"
import type { AdminCouncilInfo } from "@/gen/api"

interface UserAnalyticsSectionProps {
  councilInfos: AdminCouncilInfo[]
  onExport: (type: string, format: string) => void
}

export function UserAnalyticsSection({ councilInfos, onExport }: UserAnalyticsSectionProps) {
  // Calculate user platform data from councilInfos - aggregate across all councils
  const totalWeb = councilInfos.reduce((sum, council) => sum + (council.webCount || 0), 0)
  const totalAndroid = councilInfos.reduce((sum, council) => sum + (council.androidCount || 0), 0)
  const totalIos = councilInfos.reduce((sum, council) => sum + (council.iosCount || 0), 0)

  const userPlatformData = [
    { platform: "web", count: totalWeb },
    { platform: "android", count: totalAndroid },
    { platform: "ios", count: totalIos },
  ].filter((item) => item.count > 0)

  return (
    <div className="space-y-4 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold mb-2">End User Analytics</h2>
          <p className="text-sm text-muted-foreground">
            Platform usage and end user distribution insights
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Download className="size-4" />
              Export Reports
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => onExport("user-analytics", "csv")}>
              <FileSpreadsheet className="size-4 mr-2" />
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport("user-analytics", "pdf")}>
              <FileText className="size-4 mr-2" />
              Export as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport("user-analytics", "png")}>
              <Image className="size-4 mr-2" />
              Export as PNG
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium mb-2">User Platform Distribution</h3>
            <p className="text-sm text-muted-foreground">
              Showing total usage and download by end user
            </p>
          </div>
          <AnalyticsChart
            data={userPlatformData}
            title="User Platform Distribution"
            description="Showing total usage and download by end user"
          />
        </div>
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium mb-2">End User Distribution</h3>
            <p className="text-sm text-muted-foreground">
              End users per customer graph representation
            </p>
          </div>
          <CustomersPerCouncilChart
            data={councilInfos.map((council) => ({
              name: council.name || "",
              customers: council.userCount || 0,
              devices: council.deviceCount || 0,
            }))}
            viewType="pie"
          />
        </div>
      </div>
    </div>
  )
}
